<template>
    <div>
        <PreparingCharger
            v-if="isShowPreparing"
            :subtitle="preparingSubtitle"
        />
    </div>
</template>

<script>
import { confirmBindCardPayment, confirmBalancePayment, borrow, completePaymentOrder } from "../../api/logined";
import PreparingCharger from "../../components/loading/PreparingCharger.vue";
import { borrowWithStationBusyRetry } from "@/services/borrowRetry";

export default {
    name: "GatewayRedirect",
    components: {
        PreparingCharger
    },
    data() {
        return {
            pollTimer: null,
            pollStartTime: null,
            maxWaitMs: 30000,
            pollIntervalMs: 5000,
            isShowPreparing: false,
            borrowStationBusyWaiting: false
        };
    },
    computed: {
        preparingSubtitle() {
            return this.borrowStationBusyWaiting
                ? this.$t("m.preparingStationBusySubtitle")
                : "";
        },
    },
    async created() {
        const { status } = this.$route.query || {};
        if (String(status).toLowerCase() === "cancel" || String(status).toLowerCase() === "canceled" || String(status).toLowerCase() === "cancelled") {
            this.$router.push({
                name: "payment_select",
                query: { toast_msg: "Cancelled" }
            });
            return;
        }

        const redirect = await this.$store.dispatch('redirect/consumeRedirect')

        if (redirect && redirect.flow === 'balanceConfirmAndReturn') {
            this.handleBalanceConfirmAndReturn(redirect.context)
            return
        }
        if (redirect && redirect.flow === 'saveAndBorrow') {
            this.handleBorrow({ shouldCompletePaymentOrder: true, context: redirect.context })
            return
        }
        if (redirect && redirect.flow === 'borrow') {
            this.handleBorrow({ shouldCompletePaymentOrder: false })
            return
        }

        this.showLoading();
        this.handleConfirm();
    },
    beforeDestroy() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        this.hideLoading();
    },
    methods: {
        async handleBalanceConfirmAndReturn(context) {
            const internalId = context && context.internal_id;
            if (!internalId) {
                this.$router.push({ name: "payment_select" });
                return;
            }
            this.showLoading();
            try {
                await confirmBalancePayment({
                    internal_id: internalId,
                    request_from: 1,
                });
                this.$store.commit("SET_HIGHLIGHT_PAYMENT_METHOD", "balance");
                this.$router.push({ name: "payment_select" });
            } catch (error) {
                this.toast(this.$t("tMsg.paymentFailed") + ": " + error.message, { persistent: !!error?.message });
                this.$router.push({ name: "payment_select" });
            }
        },
        async handleBorrow({ shouldCompletePaymentOrder, context }) {
            this.isShowPreparing = true;
            try {
                if (shouldCompletePaymentOrder) {
                    const rentPaymentId = context && context.rent_payment_id;
                    if (rentPaymentId) {
                        await completePaymentOrder({
                            rent_payment_id: rentPaymentId,
                            reuse_status: 0,
                        });
                    }
                }

                await borrowWithStationBusyRetry(
                    () => borrow({
                        code: this.$store.getters['appConfig/device_code'],
                        subscription_id: context && context.subscription_id,
                    }),
                    {
                        onStationBusyChange: (waiting) => {
                            this.borrowStationBusyWaiting = waiting;
                        },
                    }
                );

                this.$router.push({ name: "borrow_success" });
            } catch (error) {
                this.toast(error.message);
            } finally {
                this.borrowStationBusyWaiting = false;
                this.isShowPreparing = false;
            }
        },
        getInternalId() {
            let internalId = this.$route.params.internalId || this.$route.params.pathMatch;
            if (internalId && internalId.startsWith("/")) {
                internalId = internalId.substring(1);
            }

            if (!internalId) {
                internalId = this.$route.query.internalId || this.$route.query.internal_id;
            }

            if (!internalId) {
                const pathParts = this.$route.path.split('/');
                if (pathParts.length > 0) {
                   const lastPart = pathParts[pathParts.length - 1];
                   if (lastPart && /^[A-Za-z]{2}\d+/.test(lastPart)) {
                       internalId = lastPart;
                   }
                }
            }
            return internalId;
        },
        async handleBindSuccess(data) {
            if (data && data.card_id) {
                this.$store.commit("SET_HIGHLIGHT_CARD_ID", data.card_id);
            }
            this.$router.push({
                name: "payment_select",
                query: { toast_msg: this.$t("tMsg.bindSuccess") }
            });
        },
        async handleConfirm() {
            try {
                const internalId = this.getInternalId();
                if (!internalId) {
                    console.error("GatewayRedirect: Missing internalId in URL path");
                    this.$router.push({ name: "payment_select" });
                    return;
                }
                const res = await confirmBindCardPayment({ internal_id: internalId });
                const data = res && res.data;
                await this.handleBindSuccess(data);
                return;
            } catch (error) {
                const code = error && error.code;
                let msg = "";
                if (code === 80804) {
                    this.startPolling();
                    return;
                }
                if (code === 80411) {
                    msg = this.$t ? this.$t(`eMsg.${code}`) : "";
                } else {
                    msg = this.$t ? this.$t("tMsg.bindFailed") : "Bind card failed";
                }
                this.$router.push({
                    name: "payment_select",
                    query: { toast_msg: msg }
                });
            }
        },
        startPolling() {
            const internalId = this.getInternalId();
            if (!internalId) {
                this.$router.push({ name: "payment_select" });
                return;
            }
            this.pollStartTime = Date.now();
            this.showLoading();
            this.pollTimer = setInterval(async () => {
                const elapsed = Date.now() - this.pollStartTime;
                if (elapsed >= this.maxWaitMs) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                    this.hideLoading();
                    const key = "tMsg.bindTimeout";
                    const fallbackMsg = this.$t ? this.$t(key) : "";
                    const msg =
                        fallbackMsg && fallbackMsg !== key
                            ? fallbackMsg
                            : "Bind card timeout, please try again";
                    this.$router.push({
                        name: "payment_select",
                        query: { toast_msg: msg }
                    });
                    return;
                }
                try {
                    const res = await confirmBindCardPayment({ internal_id: internalId });
                    const data = res && res.data;
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                    this.hideLoading();
                    await this.handleBindSuccess(data);
                } catch (error) {
                    const code = error && error.code;
                    if (code === 80804) {
                        return;
                    }
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                    this.hideLoading();
                    const fallback = this.$t ? this.$t("tMsg.bindFailed") : "Bind card failed";
                    let msg = fallback;
                    if (code === 80411 || code === 80801) {
                        const key = `eMsg.${code}`;
                        const tMsg = this.$t ? this.$t(key) : error.message;
                        msg = tMsg || fallback;
                    }
                    this.$router.push({
                        name: "payment_select",
                        query: { toast_msg: msg }
                    });
                }
            }, this.pollIntervalMs);
        }
    }
};
</script>
